/**
 * Chat Sync Channel Service
 *
 * Simple pub/sub for chat state sync between ItemChat and PopupChatWindow.
 * Works because popup shares same JS context as main window (via window.open + ReactDOM.createRoot).
 */

import type { AIMessage, Item } from '@/types';

// ============================================
// TYPES
// ============================================

export type ChatSyncMessageType =
  | 'request-sync' // Popup requests current state from main window
  | 'sync-response' // Main window responds with item + history
  | 'new-message' // New message sent from either window
  | 'clear-chat'; // Chat history cleared

export interface ChatSyncMessage {
  type: ChatSyncMessageType;
  itemId: string;
  history?: AIMessage[];
  item?: Item;
  timestamp: number;
}

type ChatSyncCallback = (message: ChatSyncMessage) => void;

// ============================================
// STATE
// ============================================

// In-memory listeners - works because popup shares same JS context as main window
let listeners: ChatSyncCallback[] = [];

// ============================================
// PUBLIC API
// ============================================

/**
 * Broadcast a chat sync message to all listeners
 */
export function broadcastChatSync(message: Omit<ChatSyncMessage, 'timestamp'>): void {
  const fullMessage: ChatSyncMessage = { ...message, timestamp: Date.now() };
  // Directly invoke all listeners (no cross-context messaging needed)
  listeners.forEach((callback) => callback(fullMessage));
}

/**
 * Listen for chat sync messages
 * @returns Cleanup function to remove the listener
 */
export function onChatSyncMessage(callback: ChatSyncCallback): () => void {
  listeners.push(callback);

  return () => {
    listeners = listeners.filter((cb) => cb !== callback);
  };
}

/**
 * Request sync for a specific item (called by popup on initialization)
 */
export function requestItemSync(itemId: string): void {
  broadcastChatSync({
    type: 'request-sync',
    itemId,
  });
}

/**
 * Respond to sync request with item data (called by main window)
 */
export function respondToSyncRequest(itemId: string, item: Item, history: AIMessage[]): void {
  broadcastChatSync({
    type: 'sync-response',
    itemId,
    item,
    history,
  });
}

/**
 * Broadcast new chat history after message sent
 */
export function broadcastNewMessage(itemId: string, history: AIMessage[]): void {
  broadcastChatSync({
    type: 'new-message',
    itemId,
    history,
  });
}

/**
 * Broadcast that chat was cleared
 */
export function broadcastChatCleared(itemId: string): void {
  broadcastChatSync({
    type: 'clear-chat',
    itemId,
  });
}

/**
 * Clear all listeners (for cleanup)
 */
export function closeChatChannel(): void {
  listeners = [];
}
